/* eslint-disable react-hooks/exhaustive-deps */
import { CircularProgress, IconButton } from '@material-ui/core';
import { TrashIcon } from '@phosphor-icons/react';
import { useAlert } from '@orientaction/hooks';
import { FC, useEffect, useMemo, useState } from 'react';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getServiceBadgeStyle } from '../../shared/constants';
import { useStyles } from '../styles';
import { ConsultantPrestation, mapConsultantPrestations, readAllowedServiceIds } from '../utils';

interface PrestationsTabProps {
  user: any;
  reloadKey?: number;
  onDeletePrestation?: (service: { id: number; name: string }) => void;
  onAddPrestation?: () => void;
}

const PrestationsTab: FC<PrestationsTabProps> = ({
  user,
  reloadKey = 0,
  onDeletePrestation,
  onAddPrestation,
}) => {
  const classes = useStyles();
  const alert = useAlert();
  const [prestations, setPrestations] = useState<ConsultantPrestation[]>([]);
  const [loading, setLoading] = useState(true);
  const consultantId = user?.id;
  const allowedServiceIds = useMemo(
    () => new Set(readAllowedServiceIds(user)),
    [user?.allowed_services]
  );
  const visiblePrestations = useMemo(
    () => prestations.filter(service => allowedServiceIds.has(Number(service.id))),
    [prestations, allowedServiceIds]
  );

  useEffect(() => {
    if (!consultantId) {
      setPrestations([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await apiServiceNative.get(
          `/api/company-user-admin-consultant-services/${consultantId}`
        );
        if (!cancelled) {
          setPrestations(mapConsultantPrestations(response));
        }
      } catch {
        if (!cancelled) {
          setPrestations([]);
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [consultantId, reloadKey]);

  if (loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.thLeft}>Prestation</th>
              <th className={classes.thCenter}>Nombre d&apos;utilisateurs rattachés</th>
              <th className={classes.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visiblePrestations.length === 0 ? (
              <tr>
                <td colSpan={3} className={classes.emptyCell}>
                  Aucune prestation rattachée
                </td>
              </tr>
            ) : (
              visiblePrestations.map(service => {
                const colors = getServiceBadgeStyle(Number(service.id));
                return (
                  <tr key={service.id}>
                    <td>
                      <span
                        className={classes.prestationBadge}
                        style={{ backgroundColor: colors.background, color: colors.color }}>
                        {service.name}
                      </span>
                    </td>
                    <td className={classes.countCell}>{service.beneficiariesCount}</td>
                    <td className={classes.actionsCell}>
                      <IconButton
                        className={classes.iconBtn}
                        aria-label="Supprimer la prestation"
                        onClick={() =>
                          onDeletePrestation?.({
                            id: Number(service.id),
                            name: service.name,
                          })
                        }>
                        <TrashIcon size={18} />
                      </IconButton>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <button type="button" className={classes.addPrestationBtn} onClick={onAddPrestation}>
        + <span>Ajouter une prestation</span>
      </button>
    </>
  );
};

export default PrestationsTab;
